import { ErrorHandler, inject, Injectable, Injector, ViewContainerRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  EMPTY,
  exhaustMap,
  filter,
  firstValueFrom,
  Observable,
  of,
  pipe,
  Subject,
  tap,
} from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import {
  ConfirmationDialogModel,
  ConfirmationDialogComponent,
  DEFAULT_USER_LOCALE,
  LOCALE_BY_LANGUAGE,
  ToastService,
} from '@shared-ui-kit';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AuthHttpService } from './auth.http.service';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import {
  ADMIN_SIDE_NAVIGATION_ITEMS,
  CARRIER_OWNER_OPERATOR_SIDE_NAVIGATION_ITEMS,
  CUSTOMER_SIDE_NAVIGATION_ITEMS,
  getCarrierFleetOwnerSideNavigationItems,
  SideNavigationItem,
} from '../constants/navigation/navigation-items.constants';
import { SessionStore } from '../session/session.store';
import { toSyncObservable } from '../_utils/to-sync-observable';
import { environment } from '@shared-app-config';
import { ConfigService } from './config.service';
import { UserService } from './user.service';
import {
  AdminPermission,
  CarrierOwnerType,
  CarrierPermission,
  CountryModel,
  Currency,
  CustomerPermission,
  PhoneFormatModel,
  PlatformType,
  UnitSystemType,
} from '@data-access';
import { v4 } from 'uuid';
import { filterNavItems } from '../constants/navigation/nav-item.utils';
import { CABINET_TYPE } from '../constants/platform-type.constants';
import { BackendErrorCode } from '../constants/backend-error-code.constants';
import { LanguageService } from './language.service';
import { ComponentType } from '@angular/cdk/overlay';
import { isChunkLoadError } from '../_utils/chunk-load-error';
import { AppVersionService } from './app-version.service';

export interface LazyDialogOptions {
  data?: unknown;
  viewContainerRef?: ViewContainerRef;
  injector?: Injector;
  /** Kept across the reload a replaced chunk triggers, so the reloaded page can reopen the dialog. */
  reopenAfterReload?: { key: string; value: string };
}

@Injectable({ providedIn: 'root' })
export class CoreService {
  private translateService = inject(TranslateService);
  private toastService = inject(ToastService);
  private authService = inject(AuthHttpService);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private userService = inject(UserService);
  private languageService = inject(LanguageService);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly appVersionService = inject(AppVersionService);

  public httpError$ = new Subject<HttpErrorResponse>();

  private cabinetType = inject(CABINET_TYPE);
  private readonly session = inject(SessionStore);
  private readonly tabId$ = toSyncObservable(this.session.tabId);
  private readonly countriesPhoneMasks$ = toSyncObservable(this.session.countriesPhoneMasks);
  private readonly loadCountriesPhoneMasks = rxMethod<void>(
    pipe(
      exhaustMap(() =>
        this.fetchCountries().pipe(
          tap((masks) => this.session.setCountriesPhoneMasks(masks)),
          catchError((error) => {
            this.showRequestError(error, 'errors.toastrErrors.phoneMasksFailed');
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  readonly tabId = this.session.tabId;

  isDevModeEnabled(): boolean {
    const userEmail = this.userService.currentUser()?.personal?.email;
    const isFr8botUser =
      !!userEmail?.endsWith('@fr8bot.ai') || !!userEmail?.endsWith('@fr8bot.com');
    return !environment.production || (environment.production && isFr8botUser);
  }

  async initializeApp(): Promise<void> {
    const tabId = v4();
    this.session.setTabId(tabId);
    return undefined;
  }

  showError(translationKey: string, interpolateParams?: any) {
    this.showToast('error', translationKey, interpolateParams);
  }

  /**
   * Toasts a failed request under `translationKey`, unless the HTTP error interceptor already
   * toasted it — it does so for every failure whose body carries a backend `message`.
   */
  showRequestError(error: unknown, translationKey: string) {
    if ((error as HttpErrorResponse)?.error?.message) return;

    this.showError(translationKey);
  }

  showWarning(translationKey: string, interpolateParams?: any) {
    this.showToast('warning', translationKey, interpolateParams);
  }

  showSuccess(translationKey: string, interpolateParams?: any) {
    this.showToast('success', translationKey, interpolateParams);
  }

  private showToast(
    type: 'success' | 'error' | 'warning' | 'info',
    translationKey: string,
    interpolateParams?: any,
  ) {
    const text: string = this.translateService.instant(translationKey + '.text', interpolateParams);
    const title: string =
      this.translateService.instant(translationKey + '.title') !== translationKey + '.title'
        ? this.translateService.instant(translationKey + '.title')
        : '';

    this.toastService[type](text, title);
  }

  selectTabId$(): Observable<string | undefined> {
    return this.tabId$;
  }

  /**
   * Toasts a backend error code. A code with no `errors.responseErrors` text falls back to
   * `fallbackKey` when the request set one (`ERROR_FALLBACK`), otherwise to the generic internal
   * error.
   */
  proceedHTTPError(
    rawError: HttpErrorResponse,
    key: string,
    data?: any,
    fallbackKey?: string | null,
  ) {
    this.httpError$.next(rawError);

    const hasTranslation =
      this.translateService.instant(`errors.responseErrors.${key}.text`) !=
        `errors.responseErrors.${key}.text` ||
      this.translateService.instant(`errors.responseErrors.${key}.title`) !=
        `errors.responseErrors.${key}.title`;

    if (hasTranslation) {
      let text = this.translateService.instant('errors.responseErrors.' + key + '.text', {
        errorData: data,
      });
      if (
        (key == BackendErrorCode.SmsGatewayError || key == BackendErrorCode.SmtpServerError) &&
        data
      ) {
        text = data;
      }
      this.toastService.error(
        text,
        this.translateService.instant('errors.responseErrors.' + key + '.title'),
        { timeOut: 5000 },
      );
    } else {
      // TODO: delete after MVP
      if (rawError.error.message === BackendErrorCode.MvpMultipleCabinetForbidden) {
        return;
      }

      if (fallbackKey) {
        this.showError(fallbackKey);
        return;
      }

      this.toastService.error(
        this.translateService.instant('errors.responseErrors.internal_error.text'),
        this.translateService.instant('errors.responseErrors.internal_error.title'),
      );
    }
  }

  updateToken(refreshToken: string): Observable<any> {
    return this.authService.reLogin(refreshToken);
  }

  navigateToLogin(): Observable<HttpEvent<any>> {
    this.router.navigate(['authorization']).then();
    return of();
  }

  openDialog<T = any>(
    component: any,
    config: MatDialogConfig,
    dialogData?: any,
    viewContainerRef?: any,
    injector?: Injector,
  ): MatDialogRef<T> {
    return this.dialog.open(component, {
      ...config,
      data: dialogData,
      viewContainerRef,
      injector,
    });
  }

  /**
   * A chunk that fails to load goes to the `ErrorHandler`, which reloads the page: the click that
   * opened the dialog returns a promise nobody awaits, so a rejection would never reach it.
   */
  async openLazyDialog(
    loadComponent: () => Promise<ComponentType<unknown>>,
    config: MatDialogConfig,
    options: LazyDialogOptions = {},
  ): Promise<MatDialogRef<unknown> | undefined> {
    let component: ComponentType<unknown>;
    try {
      component = await loadComponent();
    } catch (error) {
      this.errorHandler.handleError(error);
      this.rememberReopen(error, options.reopenAfterReload);
      return undefined;
    }

    return this.dialog.open(component, {
      ...config,
      data: options.data,
      viewContainerRef: options.viewContainerRef,
      injector: options.injector,
    });
  }

  async showConfirmation(
    data: ConfirmationDialogModel,
    config?: MatDialogConfig,
  ): Promise<boolean | undefined> {
    const confirmationDialog$ = this.dialog.open(ConfirmationDialogComponent, {
      ...this.configService.confirmationDialogConfig,
      ...(config && { ...config }),
      data: {
        ...data,
        dialogStyle: data.dialogStyle || 'info',
        icon: data.icon || 'alert-50',
      },
    });

    return firstValueFrom(confirmationDialog$.afterClosed()).then((data) => {
      return data;
    });
  }

  getCountries(): Observable<PhoneFormatModel[]> {
    return this.countriesPhoneMasks$.pipe(
      tap((masks) => {
        if (!masks) {
          this.loadCountriesPhoneMasks();
        }
      }),
      filter((masks): masks is PhoneFormatModel[] => !!masks),
    );
  }

  fetchCountries(): Observable<PhoneFormatModel[]> {
    return this.http.get<PhoneFormatModel[]>(`${environment.apiUrl}/settings/phones`);
  }

  /**
   * BCP-47 locale that drives every date format in the app.
   *
   * It follows the language the user was actually given — the one they picked, or the one
   * an admin set on their carrier/customer — and that language decides the region too,
   * since the backend stores no region. An admin has no cabinet to inherit from, so they
   * stay on en-US until they pick a language themselves.
   *
   * The browser is deliberately absent everywhere: a Canadian browser must not silently
   * turn an English user's dates into ISO.
   */
  getUserLocale(): string {
    const language = this.languageService.selectedLanguage();

    return (language && LOCALE_BY_LANGUAGE[language]) || DEFAULT_USER_LOCALE;
  }

  getUserIanaTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC';
  }

  checkAdminPermission(requestedPermissions: AdminPermission[]): boolean {
    const user = this.userService.currentUser();
    const adminPermissions = user?.access?.admin?.permissions ?? [];

    return (
      !!adminPermissions.length &&
      requestedPermissions.some((per) => adminPermissions.some((el) => el == per))
    );
  }

  // TODO: Vit: Without customerId in MVP, until multi-cabinet will be implemented
  checkCustomerPermission(requestedPermissions: CustomerPermission[]): boolean {
    const user = this.userService.currentUser();
    const enabledPermissions = user?.access?.customers?.[0]?.permissions ?? [];

    return (
      !!enabledPermissions.length &&
      requestedPermissions.some((per) => enabledPermissions.some((el) => el == per))
    );
  }

  // TODO: Vit: Without carrierId in MVP, until multi-cabinet will be implemented
  checkCarrierPermission(requestedPermissions: CarrierPermission[]): boolean {
    const user = this.userService.currentUser();
    const carrierOwnerType = user?.access?.carriers?.[0]?.carrierOwnerType;

    let enabledPermissions;
    if (carrierOwnerType === CarrierOwnerType.OwnerOperator) {
      enabledPermissions =
        user?.access?.carriers?.[0]?.permissions?.filter(
          (p) => p !== CarrierPermission.FleetBoardRead && p !== CarrierPermission.FleetBoardFull,
        ) ?? [];
    } else {
      enabledPermissions = user?.access?.carriers?.[0]?.permissions ?? [];
    }

    return (
      !!enabledPermissions.length &&
      requestedPermissions.some((per) => enabledPermissions.some((el) => el == per))
    );
  }

  // TODO: #1012 Fix after multi-cabinet implemented
  getUnitSystem(): UnitSystemType {
    const user = this.userService.currentUser();

    switch (this.cabinetType) {
      case PlatformType.WebCarrier:
        return user?.access?.carriers?.[0]?.localization?.unitSystem ?? UnitSystemType.Imperial;
      case PlatformType.WebCustomer:
        return user?.access?.customers?.[0]?.localization?.unitSystem ?? UnitSystemType.Imperial;
      default:
        return UnitSystemType.Imperial;
    }
  }

  // TODO: #978-currency Fix after multi-cabinet implemented
  getCurrency(): Currency {
    const user = this.userService.currentUser();

    switch (this.cabinetType) {
      case PlatformType.WebCarrier:
        return user?.access?.carriers?.[0]?.localization?.currency ?? Currency.Usd;
      case PlatformType.WebCustomer:
        return user?.access?.customers?.[0]?.localization?.currency ?? Currency.Usd;
      default:
        return Currency.Usd;
    }
  }

  // TODO: Fix after multi-cabinet implemented
  /** Countries the current cabinet is enabled to operate in. */
  getAvailableCountries(): CountryModel[] {
    const user = this.userService.currentUser();

    const countries =
      this.cabinetType === PlatformType.WebCustomer
        ? user?.access?.customers?.[0]?.localization?.countries
        : user?.access?.carriers?.[0]?.localization?.countries;

    return (countries ?? []).filter((country) => country.enabled);
  }

  /** Cabinet-agnostic permission check */
  checkPermission(cabinet: PlatformType, requestedPermissions: string[]): boolean {
    switch (cabinet) {
      case PlatformType.WebAdmin:
        return this.checkAdminPermission(requestedPermissions as AdminPermission[]);
      case PlatformType.WebCarrier:
        return this.checkCarrierPermission(requestedPermissions as CarrierPermission[]);
      case PlatformType.WebCustomer:
        return this.checkCustomerPermission(requestedPermissions as CustomerPermission[]);
      default:
        return false;
    }
  }

  getAdminNavItems(): SideNavigationItem[] {
    const user = this.userService.currentUser();
    const permissions = user?.access?.admin?.permissions ?? [];
    return filterNavItems(ADMIN_SIDE_NAVIGATION_ITEMS, permissions);
  }

  /** OWNER_OPERATOR - doesn't have access to fleet board */
  /** FLEET_OWNER and CARRIER have */
  getCarrierNavItems(): SideNavigationItem[] {
    const user = this.userService.currentUser();
    const carrierOwnerType = user?.access?.carriers?.[0]?.carrierOwnerType;
    const permissions = user?.access?.carriers?.[0]?.permissions ?? [];

    if (carrierOwnerType === CarrierOwnerType.OwnerOperator) {
      return filterNavItems(CARRIER_OWNER_OPERATOR_SIDE_NAVIGATION_ITEMS, permissions);
    }

    return filterNavItems(
      getCarrierFleetOwnerSideNavigationItems(this.isDevModeEnabled()),
      permissions,
    );
  }

  getCustomerNavItems(): SideNavigationItem[] {
    const user = this.userService.currentUser();
    const permissions = user?.access?.customers?.[0]?.permissions ?? [];
    return filterNavItems(CUSTOMER_SIDE_NAVIGATION_ITEMS, permissions);
  }

  getNavItems(cabinet: PlatformType): SideNavigationItem[] {
    switch (cabinet) {
      case PlatformType.WebAdmin:
        return this.getAdminNavItems();
      case PlatformType.WebCarrier:
        return this.getCarrierNavItems();
      case PlatformType.WebCustomer:
        return this.getCustomerNavItems();
      default:
        return [];
    }
  }

  getFirstAllowedRoute(cabinet: PlatformType): string {
    return this.getNavItems(cabinet)[0]?.routerLink ?? '';
  }

  /** Only when the error handler has started a reload — a throttled one showed a toast instead. */
  private rememberReopen(error: unknown, reopen: LazyDialogOptions['reopenAfterReload']): void {
    if (reopen && isChunkLoadError(error) && this.appVersionService.isReloading()) {
      this.appVersionService.rememberForReload(reopen.key, reopen.value);
    }
  }
}
